import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faMagnifyingGlass, faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { findVietQrBank, searchVietQrBanks, VIETQR_BANKS } from '../../utils/vietQr';

interface BankSelectProps {
    /** The saved bank name. Free text on profiles written before this picker existed. */
    value: string;
    /** Always one of the VietQR short names — never what the user typed. */
    onChange: (bankName: string) => void;
    disabled?: boolean;
    /** Classes for the trigger, so each form keeps the look of its other fields. */
    className?: string;
    /** Names the control for screen readers — usually the same text as the field's label. */
    ariaLabel?: string;
}

/**
 * Picks the bank from the VietQR (NAPAS) list instead of letting it be typed.
 *
 * The transfer QR is built from the bank's BIN, and the BIN is looked up from
 * this name — so a typo ("Vietcom bank", "NH Quân đội") used to save fine and
 * then silently produce no QR at all, or the payer would scan a code for the
 * wrong bank. Picking from the list makes that impossible: whatever is stored
 * always resolves back to a BIN.
 *
 * The list opens as its own overlay rather than a dropdown anchored to the
 * field. The picker is used from inside a scrollable modal, where an absolutely
 * positioned panel gets clipped, and on a phone a full-height sheet fits far
 * more than 50 banks than a panel squeezed under an input does.
 */
const BankSelect: React.FC<BankSelectProps> = ({
                                                   value,
                                                   onChange,
                                                   disabled = false,
                                                   className = 'input-field',
                                                   ariaLabel
                                               }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [query, setQuery] = useState<string>('');
    const searchRef = useRef<HTMLInputElement>(null);

    // What is stored resolves to a real bank for anything picked here; it only
    // fails for a name typed before the picker, which is what the warning is for.
    const selected = useMemo(() => findVietQrBank(value), [value]);
    const results = useMemo(() => searchVietQrBanks(query), [query]);

    useEffect(() => {
        if (!isOpen) return;
        // Typing is how you find a bank in a list this long, so the box is ready
        // immediately — on desktop. On touch, focusing here would throw up the
        // keyboard over the list before a single bank has been seen.
        const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
        if (!isTouch) searchRef.current?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setIsOpen(false);
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [isOpen]);

    const open = () => {
        setQuery('');
        setIsOpen(true);
    };

    const pick = (bankName: string) => {
        onChange(bankName);
        setIsOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={open}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={ariaLabel}
                className={`${className} flex items-center justify-between gap-2 text-left disabled:opacity-60`}
            >
                {selected ? (
                    <span className="min-w-0">
                        <span className="block font-medium truncate">{selected.name}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{selected.fullName}</span>
                    </span>
                ) : value ? (
                    // A name saved before the picker that matches no bank: keep it
                    // visible so the user recognises it, and say why it must change.
                    <span className="min-w-0">
                        <span className="block font-medium truncate">{value}</span>
                        <span className="block text-xs text-amber-600 dark:text-amber-400 truncate">
                            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
                            {t('profile.components.bankSelect.unknownBank')}
                        </span>
                    </span>
                ) : (
                    <span className="text-gray-500 dark:text-gray-400">{t('profile.components.bankSelect.placeholder')}</span>
                )}
                <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 shrink-0" aria-hidden="true" />
            </button>

            {/* Rendered via portal to body: the form this sits in is itself a
                .glass-modal, and its backdrop-filter makes it the containing
                block for fixed children — the overlay would be positioned
                against that modal and clipped by its scroll box instead of
                covering the viewport. */}
            {isOpen && createPortal(
                <div
                    className="glass-modal-overlay !z-[70]"
                    onClick={() => setIsOpen(false)}
                    role="presentation"
                >
                    <div
                        className="glass-modal max-w-md w-full !overflow-hidden flex flex-col !max-h-[80vh]"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('profile.components.bankSelect.title')}
                    >
                        <div className="p-4 border-b border-gray-200/60 dark:border-gray-700/60">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    {t('profile.components.bankSelect.title')}
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    aria-label={t('profile.components.bankSelect.close')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                            <div className="relative">
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                                    aria-hidden="true"
                                />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    className="input-field !pl-9"
                                    placeholder={t('profile.components.bankSelect.searchPlaceholder')}
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto p-2" role="listbox" aria-label={t('profile.components.bankSelect.title')}>
                            {results.length === 0 ? (
                                <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                    {t('profile.components.bankSelect.noResults', { query })}
                                </p>
                            ) : (
                                results.map((bank) => {
                                    const isSelected = selected?.bin === bank.bin;
                                    return (
                                        <button
                                            key={bank.bin}
                                            type="button"
                                            role="option"
                                            aria-selected={isSelected}
                                            onClick={() => pick(bank.name)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                                                isSelected
                                                    ? 'bg-blue-50 dark:bg-blue-900/30'
                                                    : 'hover:bg-gray-100/70 dark:hover:bg-gray-700/40'
                                            }`}
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">
                                                    {bank.name}
                                                </span>
                                                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                                    {bank.fullName}
                                                </span>
                                            </span>
                                            {isSelected && (
                                                <FontAwesomeIcon icon={faCheck} className="text-blue-600 dark:text-blue-400 shrink-0" />
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        <div className="px-4 py-2 border-t border-gray-200/60 dark:border-gray-700/60 text-xs text-gray-500 dark:text-gray-400">
                            {t('profile.components.bankSelect.count', { shown: results.length, total: VIETQR_BANKS.length })}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default BankSelect;
